import { useQuery } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { Address } from 'viem';

import { LaunchpadSalePoolType } from '@api';

import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import {
  BaseTokenDetails,
  DEFAULT_TARGET_CHAIN,
  LAST_FCFS_CONTRACT_VERSION,
  LAST_PRO_RATA_CONTRACT_VERSION,
  LAUNCHPAD_DEPOSIT_CONTRACTS,
} from '@shared/constants';
import { getTokenInfo, TokenInfo } from '@shared/helpers/web3/erc20';

import { isProRataType } from '@launchpad/utils/contract';

export const getDepositTokenInfo = (token: TokenInfo | undefined): BaseTokenDetails | undefined => {
  if (!token) {
    return;
  }

  return {
    address: token.address,
    symbol: token.symbol || 'N/A',
    decimals: token.decimals,
    chainId: DEFAULT_TARGET_CHAIN.id,
    chain: DEFAULT_TARGET_CHAIN,
  };
};

export const getDepositToken = async ({
  type,
  depositContractAddress,
}: {
  type?: LaunchpadSalePoolType;
  depositContractAddress: string | null | undefined;
}): Promise<TokenInfo | undefined> => {
  if (isProRataType(type)) {
    const tokenAddress = await readContract(wagmiConfig, {
      ...LAUNCHPAD_DEPOSIT_CONTRACTS.PRO_RATA[LAST_PRO_RATA_CONTRACT_VERSION],
      chainId: DEFAULT_TARGET_CHAIN.id,
      address: depositContractAddress as Address,
      functionName: 'baseToken',
    });

    return getTokenInfo({ tokenAddress });
  }

  const tokenAddress = await readContract(wagmiConfig, {
    ...LAUNCHPAD_DEPOSIT_CONTRACTS.FCFS[LAST_FCFS_CONTRACT_VERSION],
    chainId: DEFAULT_TARGET_CHAIN.id,
    address: depositContractAddress as Address,
    functionName: 'erc20Token',
  });

  return getTokenInfo({ tokenAddress });
};

const useDepositTokenQuery = ({
  depositContractAddress,
  type,
}: {
  depositContractAddress?: string | null;
  type?: LaunchpadSalePoolType;
}) => {
  return useQuery({
    queryFn: () => getDepositToken({ type, depositContractAddress }),
    select: (data) => getDepositTokenInfo(data),
    queryKey: ['depositTokenInfo', depositContractAddress, type],
    staleTime: 0,
    enabled: !!depositContractAddress,
  });
};

export default useDepositTokenQuery;
